import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { operations, retention } from '../services/api';

// Perfil 360º do sócio — visão operacional única, capability-aware (tabs só aparecem se o módulo
// respetivo estiver ativo no ginásio). O backend já decide o que devolve; aqui apresentamos.
export default function Member360Page() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Resumo');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [risk, setRisk] = useState(null); // null = desconhecido/Retention OFF (403) → não mostra banner

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const res = await operations.member360(id); if (alive) { setData(res.data); setLoading(false); } }
      catch (e) { if (alive) { setNotFound(e?.response?.status === 404); setLoading(false); } }
    })();
    // Retenção é capability-aware: se o módulo estiver OFF o endpoint devolve 403 → simplesmente não mostramos.
    (async () => {
      try { const r = await retention.member(id); if (alive) setRisk(r.data); }
      catch { /* 403/erro → sem banner de risco */ }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div style={S.wrap}><p>A carregar…</p></div>;
  if (notFound || !data) return <div style={S.wrap}><p>Membro não encontrado.</p></div>;

  const h = data.header || {};
  const tabs = data.tabs || ['Resumo'];

  return (
    <div style={S.wrap}>
      {/* Cabeçalho */}
      <div style={S.head}>
        <div style={S.avatar}>{(h.name || '?').slice(0, 1).toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <div style={S.name}>{h.name} <span style={{ ...S.chip, background: h.isActive ? '#dcfce7' : '#fee2e2', color: h.isActive ? '#166534' : '#991b1b' }}>{h.isActive ? 'Ativo' : 'Inativo'}</span></div>
          <div style={S.meta}>Nº {h.id} · {h.email}{h.phone ? ` · ${h.phone}` : ''}</div>
          {data.financial && <div style={{ ...S.chip, marginTop: 6, background: data.financial.regularizado ? '#dcfce7' : '#fef3c7', color: data.financial.regularizado ? '#166534' : '#92400e' }}>
            {data.financial.regularizado ? 'Regularizado' : `Dívida: ${Number(data.financial.outstanding).toFixed(2)}€`}
          </div>}
        </div>
      </div>

      {/* Banner de risco (só se Retention ON) — nível + razões, nunca só um número */}
      {risk && <RiskBanner risk={risk} />}

      {/* Tabs (só as ativas) */}
      <div style={S.tabs}>
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}>{t}</button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'Resumo' && <Summary data={data} />}
        {tab === 'Acessos' && <Access access={data.access} />}
        {tab === 'Avença' && <Avenca avenca={data.avenca} />}
        {tab === 'Agenda' && <Agenda agenda={data.agenda} />}
        {tab === 'Wearables' && <Fitness fitness={data.fitness} />}
        {tab === 'Financeiro' && <Financial financial={data.financial} />}
        {tab === 'Comunicações' && <Comms comms={data.comms} />}
        {tab === 'PT' && <Pt pt={data.pt} />}
        {tab === 'Treinos' && <Training training={data.training} />}
        {tab === 'Avaliações' && <Assessment assessment={data.assessment} />}
        {tab === 'Progresso' && <Progress progress={data.progress} />}
      </div>
    </div>
  );
}

const RISK_TONES = {
  critical: ['Crítico', '#b91c1c', '#fef2f2', '#fecaca'],
  high: ['Alto', '#c2410c', '#fff7ed', '#fed7aa'],
  medium: ['Médio', '#a16207', '#fefce8', '#fde68a'],
  low: ['Baixo', '#15803d', '#f0fdf4', '#bbf7d0'],
};
function RiskBanner({ risk }) {
  const [label, color, bg, border] = RISK_TONES[risk.level] || RISK_TONES.low;
  return (
    <div style={{ ...S.riskBanner, background: bg, borderColor: border }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...S.riskChip, background: color }}>Risco {label} · {risk.score}</span>
        <div style={S.riskReasons}>{(risk.reasons || []).map((r, i) => <span key={i} style={S.riskReason}>{r}</span>)}</div>
      </div>
      <div style={S.riskMeta}>
        Última atividade: {fmtDate(risk.lastActivityAt) || '—'} · Confiança dos dados: {({ full: 'completa', partial: 'parcial', limited: 'limitada' }[risk.dataConfidence]) || '—'}
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div style={S.card}><div style={S.cardTitle}>{title}</div>{children}</div>
);
const Empty = ({ label }) => <div style={S.empty}>Sem dados de {label} ainda.</div>;

function Summary({ data }) {
  return (
    <div style={S.grid}>
      {data.avenca && <Card title="Avença"><div>{data.avenca.status}</div><div style={S.sub}>Validade: {fmtDate(data.avenca.endDate) || '—'}</div></Card>}
      {data.access && <Card title="Acesso"><div>Última entrada: {fmtDate(data.access.lastEntry) || '—'}</div><div style={S.sub}>{data.access.entries30d} entradas (30d)</div></Card>}
      {data.agenda && <Card title="Próximas aulas">{(data.agenda.length ? data.agenda : []).slice(0, 3).map((a, i) => <div key={i} style={S.sub}>{a.name} · {fmtDate(a.start)}</div>) || <div style={S.sub}>—</div>}{!data.agenda.length && <div style={S.sub}>—</div>}</Card>}
      {data.fitness && <Card title="Fitness"><div>{data.fitness.connected ? 'Wearable ligado' : 'Sem wearable'}</div><div style={S.sub}>{(data.fitness.recentWorkouts || []).length} treinos recentes</div></Card>}
      {data.pt && <Card title="PT"><div>Restantes: {data.pt.remaining ?? '—'}</div><div style={S.sub}>Próxima: {data.pt.nextSession ? fmtDate(data.pt.nextSession.scheduledAt) : '—'}</div></Card>}
    </div>
  );
}
const Access = ({ access }) => access ? (
  <Card title="Acessos"><div>Última entrada: {fmtDate(access.lastEntry) || '—'}</div><div style={S.sub}>{access.entries30d} entradas nos últimos 30 dias</div>
    {(access.recentDenied || []).length > 0 && <div style={{ marginTop: 8 }}>{access.recentDenied.map((d, i) => <div key={i} style={{ color: 'var(--error)', fontSize: 13 }}>Recusa: {d.reason} · {fmtDate(d.timestamp)}</div>)}</div>}
  </Card>) : <Empty label="acessos" />;
const Avenca = ({ avenca }) => avenca ? <Card title="Avença"><div>{avenca.status}</div><div style={S.sub}>Início: {fmtDate(avenca.startDate)} · Validade: {fmtDate(avenca.endDate) || '—'}</div>{avenca.nextGenerationDate && <div style={S.sub}>Próxima cobrança: {fmtDate(avenca.nextGenerationDate)}</div>}</Card> : <Empty label="avença" />;
const Agenda = ({ agenda }) => <Card title="Agenda">{(agenda || []).length ? agenda.map((a, i) => <div key={i} style={S.sub}>{a.name} · {fmtDate(a.start)}</div>) : <Empty label="agenda" />}</Card>;
const Fitness = ({ fitness }) => fitness ? <Card title="Wearables"><div>{fitness.connected ? 'Ligado' : 'Sem ligação'}</div>{(fitness.recentWorkouts || []).map((w, i) => <div key={i} style={S.sub}>{w.type} · {Math.round(w.calories)} kcal · {fmtDate(w.startAt)}</div>)}</Card> : <Empty label="wearables" />;
const Financial = ({ financial }) => financial ? <Card title="Financeiro"><div>{financial.regularizado ? 'Regularizado' : `Em dívida: ${Number(financial.outstanding).toFixed(2)}€`}</div>{financial.hasOverdue && <div style={{ color: 'var(--error)', fontSize: 13 }}>Tem valores vencidos</div>}</Card> : <Empty label="financeiro" />;
const Comms = ({ comms }) => <Card title="Comunicações">{(comms || []).length ? comms.map((m, i) => <div key={i} style={S.sub}>{m.channel} · {m.status} · {fmtDate(m.createdAt)}</div>) : <Empty label="comunicações" />}</Card>;
const Pt = ({ pt }) => pt ? (
  <Card title="Personal Training">
    <div>Trainer: {pt.trainerId ? `#${pt.trainerId}` : '—'}</div>
    <div style={S.sub}>Sessões restantes: {pt.remaining ?? '—'}</div>
    <div style={S.sub}>Validade: {fmtDate(pt.validUntil) || 'sem termo'}</div>
    <div style={S.sub}>Próxima sessão: {pt.nextSession ? fmtDate(pt.nextSession.scheduledAt) : '—'}</div>
  </Card>
) : <Empty label="PT" />;
const Training = ({ training }) => training ? (
  <Card title="Treinos">
    <div>Plano ativo: {training.activePlan ? `#${training.activePlan.workoutPlanId} (v${training.activePlan.version})` : 'nenhum'}</div>
    <div style={S.sub}>Última execução: {fmtDate(training.lastExecution) || '—'}</div>
  </Card>
) : <Empty label="treinos" />;
const Assessment = ({ assessment }) => assessment ? (
  <Card title="Última avaliação">
    <div>{fmtDate(assessment.date)}</div>
    <div style={S.sub}>Peso: {assessment.weightKg ?? '—'} kg · Altura: {assessment.heightCm ?? '—'} cm{assessment.bodyFatPercent != null ? ` · MG: ${assessment.bodyFatPercent}%` : ''}</div>
  </Card>
) : <Empty label="avaliações" />;
const Progress = ({ progress }) => progress ? (
  <Card title="Progresso (peso)">
    <div>{Number(progress.first)} → {Number(progress.last)}</div>
    <div style={S.sub}>Variação: {Number(progress.delta) > 0 ? '+' : ''}{Number(progress.delta).toFixed(1)}</div>
  </Card>
) : <Empty label="progresso" />;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : null;

const S = {
  wrap: { maxWidth: 960, margin: '0 auto', padding: 16 },
  head: { display: 'flex', gap: 16, alignItems: 'center', background: 'var(--info-bg)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 16 },
  avatar: { width: 56, height: 56, borderRadius: '50%', background: 'var(--info)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700 },
  name: { fontSize: 20, fontWeight: 700 },
  meta: { color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 },
  chip: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, marginLeft: 8 },
  riskBanner: { marginTop: 12, border: '1px solid', borderRadius: 12, padding: '12px 14px' },
  riskChip: { color: '#fff', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  riskReasons: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  riskReason: { background: 'rgba(255,255,255,.7)', color: 'var(--info-text)', borderRadius: 6, padding: '2px 8px', fontSize: 12 },
  riskMeta: { color: 'var(--text-secondary)', fontSize: 12, marginTop: 8 },
  tabs: { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 16, borderBottom: '1px solid var(--border-color)' },
  tab: { padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, borderBottom: '2px solid transparent' },
  tabActive: { color: 'var(--info-text)', fontWeight: 600, borderBottom: '2px solid var(--info)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  card: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 14 },
  cardTitle: { fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-secondary)', marginBottom: 6 },
  sub: { color: 'var(--info-text)', fontSize: 14, marginTop: 2 },
  empty: { color: 'var(--text-muted)', fontSize: 14, padding: 12 },
};
